/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量打标模式底部固定工具栏
 *
 * 进入批量模式后由 PhotosList 渲染在页面底部。
 * 内容: 已选 N 张 + 全选 + 取消 + 批量移除 + 应用标签 + (点应用/移除后弹) TagPicker modal。
 *
 * 数据流:
 *   - 已选数 / 全选状态 → 从 redux 的 selectionConfig 读 (跟 BatchAddLabelButton 共享同一份)
 *   - 应用标签 → 调 context.applyTags(),具体实现由 Provider 注入 (各 PhotosList 一份)
 *   - 批量移除 → 调 context.unbindTags()
 *   - 全选按钮 → dispatch toggleCheckAll 等动作,由 props 注入,避免本组件依赖具体 slice
 *   - 取消 → 调 context.exit() → Provider 内部触发 onExit (一般清空 selected)
 *
 * 埋点 (v3): 进入批量模式 / 全选 / 取消 / 应用 / 移除 / picker 内点击 / result / toast 都在这里上报
 */
import React, { useCallback, useState } from 'react'
import { message } from 'antd'
import {
  CheckSquareOutlined,
  CloseOutlined,
  MinusCircleOutlined,
  MinusSquareOutlined,
  TagOutlined
} from '@ant-design/icons'
import TagPicker from '../TagPicker'
import { useBatchTagMode } from './context'
import { invalidateTeamPhotoTagsCache } from '../../../app/hooks/useTeamPhotoTags'
import {
  trackPicspageClick,
  trackPicspageDialogShow,
  trackPicspageDialogClick,
  trackPicspageToast,
  trackPicspageResult
} from '../../../pages/photoTag/utils/track'
import './BatchTagBar.scss'

export interface BatchTagBarProps {
  /** 当前已选照片数 (来自 redux selectionConfig.checkedPhotos.size 或 checkAll ? 总数 : 0) */
  selectedCount: number
  /** 当前 group ID,失效缓存用 */
  groupID: string
  /** 当前 user ID,失效缓存用 */
  userID: string
  /** 全选按钮回调 (一般 dispatch toggleCheckAll / setCheckAll(true)) */
  onSelectAll: () => void
  /** 是否处于"全选"状态 (来自 redux),用于显示反向操作"取消全选" */
  isSelectAll?: boolean
}

const BatchTagBar: React.FC<BatchTagBarProps> = ({
  selectedCount,
  groupID,
  userID,
  onSelectAll,
  isSelectAll
}) => {
  const { isBatchMode, exit, applyTags, unbindTags, fromPage } =
    useBatchTagMode()
  const [pickerMode, setPickerMode] = useState<'add' | 'remove' | null>(null)
  const pickerVisible = pickerMode !== null
  const [submitting, setSubmitting] = useState(false)

  const handleSelectAll = useCallback(() => {
    // 埋点: click batch_select_all / batch_unselect_all
    trackPicspageClick({
      clickItem: isSelectAll ? 'batch_unselect_all' : 'batch_select_all',
      fromPage,
      number1: selectedCount
    })
    onSelectAll()
  }, [isSelectAll, onSelectAll, fromPage, selectedCount])

  const handleCancel = useCallback(() => {
    // 埋点: click batch_cancel
    trackPicspageClick({
      clickItem: 'batch_cancel',
      fromPage,
      number1: selectedCount
    })
    exit()
  }, [exit, fromPage, selectedCount])

  const handleApplyClick = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('请先选择照片')
      trackPicspageToast({ toastName: 'batch_no_selection', fromPage })
      return
    }
    // 埋点: click batch_apply_label + dialog_show batch_select_labels_add
    trackPicspageClick({
      clickItem: 'batch_apply_label',
      fromPage,
      number1: selectedCount
    })
    trackPicspageDialogShow({
      popTitle: 'batch_select_labels_add',
      fromPage,
      number1: selectedCount
    })
    setPickerMode('add')
  }, [selectedCount, fromPage])

  const handleRemoveClick = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('请先选择照片')
      trackPicspageToast({ toastName: 'batch_no_selection', fromPage })
      return
    }
    // 埋点: click batch_remove_open + dialog_show batch_select_labels_remove
    trackPicspageClick({
      clickItem: 'batch_remove_open',
      fromPage,
      number1: selectedCount
    })
    trackPicspageDialogShow({
      popTitle: 'batch_select_labels_remove',
      fromPage,
      number1: selectedCount
    })
    setPickerMode('remove')
  }, [selectedCount, fromPage])

  const handlePickerOK = useCallback(
    async (labelIDs: string[], _matchAll: boolean, labelNames?: string[]) => {
      const popTitle =
        pickerMode === 'remove'
          ? 'batch_select_labels_remove'
          : 'batch_select_labels_add'
      // 埋点: dialog_click confirm
      trackPicspageDialogClick({
        popTitle,
        clickItem: 'confirm',
        fromPage
      })
      if (labelIDs.length === 0) {
        message.warning('请至少选择一个标签')
        trackPicspageToast({ toastName: 'batch_no_label_selected', fromPage })
        return
      }
      setSubmitting(true)
      const t0 = Date.now()
      const names = labelNames || []
      try {
        if (pickerMode === 'add') {
          const result = await applyTags({
            labelIDs,
            labelNames: names
          })
          message.success(
            `成功为 ${result.boundPhotoCount} 张照片添加了 ${result.boundLabelCount} 个标签`
          )
          trackPicspageResult({
            actionName: 'bind_label_batch',
            actionResult: 'success',
            costTime: Date.now() - t0,
            photoNum: result.boundPhotoCount,
            number1: labelIDs.length,
            list1: names,
            tabName: 'team_label',
            bool1: true,
            fromPage
          })
          trackPicspageToast({ toastName: 'batch_bind_success', fromPage })
        } else {
          const result = await unbindTags({
            labelIDs,
            labelNames: names
          })
          message.success(
            `成功从 ${result.affectedPhotoCount} 张照片移除了 ${result.affectedLabelCount} 个标签 (实际清除 ${result.removedRelCount} 条关联)`
          )
          trackPicspageResult({
            actionName: 'unbind_label_batch',
            actionResult: 'success',
            costTime: Date.now() - t0,
            photoNum: result.affectedPhotoCount,
            number1: labelIDs.length,
            list1: names,
            tabName: 'team_label',
            fromPage
          })
          trackPicspageToast({ toastName: 'batch_unbind_success', fromPage })
        }
        invalidateTeamPhotoTagsCache(groupID, userID)
        setPickerMode(null)
        exit()
      } catch (err) {
        const msg =
          (err as Error)?.message ||
          (pickerMode === 'add'
            ? '批量打标失败,请稍后再试'
            : '批量移除失败,请稍后再试')
        message.error(msg)
        trackPicspageResult({
          actionName:
            pickerMode === 'add' ? 'bind_label_batch' : 'unbind_label_batch',
          actionResult: 'fail',
          costTime: Date.now() - t0,
          errorMsg: msg,
          photoNum: selectedCount,
          number1: labelIDs.length,
          list1: names,
          tabName: 'team_label',
          bool1: pickerMode === 'add' ? true : undefined,
          fromPage
        })
        trackPicspageToast({
          toastName:
            pickerMode === 'add' ? 'batch_bind_fail' : 'batch_unbind_fail',
          fromPage
        })
      } finally {
        setSubmitting(false)
      }
    },
    [
      pickerMode,
      applyTags,
      unbindTags,
      exit,
      groupID,
      userID,
      fromPage,
      selectedCount
    ]
  )

  if (!isBatchMode) return null

  return (
    <>
      <div className="batch-tag-bar">
        <span className="btb-count">
          已选 <b>{selectedCount}</b> 张
        </span>
        <span className="btb-divider" />
        <button
          type="button"
          className="btb-btn"
          onClick={handleSelectAll}
          disabled={submitting}
        >
          {isSelectAll ? <MinusSquareOutlined /> : <CheckSquareOutlined />}
          {isSelectAll ? '取消全选' : '全选'}
        </button>
        <button
          type="button"
          className="btb-btn"
          onClick={handleCancel}
          disabled={submitting}
        >
          <CloseOutlined />
          取消
        </button>
        <button
          type="button"
          className="btb-btn"
          onClick={handleRemoveClick}
          disabled={selectedCount === 0 || submitting}
        >
          <MinusCircleOutlined />
          批量移除
        </button>
        <button
          type="button"
          className="btb-btn btb-btn--primary"
          onClick={handleApplyClick}
          disabled={selectedCount === 0 || submitting}
        >
          <TagOutlined />
          应用标签
        </button>
      </div>
      <TagPicker
        visible={pickerVisible}
        checkedList={[]}
        matchAll={false}
        hideMatchLogic
        // 「一张照片只能 1 个标签」: add 模式强制单选;remove 模式不限 (no-op safe)
        singleSelect={pickerMode === 'add'}
        title={
          pickerMode === 'remove' ? '批量移除照片标签' : '批量添加照片标签'
        }
        okText={
          submitting
            ? '提交中...'
            : pickerMode === 'remove'
              ? `从 ${selectedCount} 张移除`
              : `应用到 ${selectedCount} 张`
        }
        fromPlace={
          pickerMode === 'remove'
            ? '全部照片批量移除照片页'
            : '全部照片批量添加照片页'
        }
        onCancel={() => {
          if (submitting) return
          // 埋点: dialog_click cancel
          if (pickerMode) {
            trackPicspageDialogClick({
              popTitle:
                pickerMode === 'remove'
                  ? 'batch_select_labels_remove'
                  : 'batch_select_labels_add',
              clickItem: 'cancel',
              fromPage
            })
          }
          setPickerMode(null)
        }}
        onOK={handlePickerOK}
        onSearch={() => {
          if (!pickerMode) return
          trackPicspageDialogClick({
            popTitle:
              pickerMode === 'remove'
                ? 'batch_select_labels_remove'
                : 'batch_select_labels_add',
            clickItem: 'search',
            fromPage
          })
        }}
      />
    </>
  )
}

export default BatchTagBar
